import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { ApiService } from "../../api";
import DesignCanvas from "../../components/formdesign/DesignCanvas";
import FieldInspector from "../../components/formdesign/FieldInspector";
import SchemaPreview from "../../components/formdesign/SchemaPreview";
import {
  LAYOUT_WIDGETS,
  WIDGETS,
  widgetId,
  allKeys,
  find,
  isContainer,
  isTable,
  locate,
  makeColumn,
  makeContainer,
  makeField,
  moveNode,
  parse,
  removeNode,
  serialise,
  type Doc,
  type Node,
} from "../../components/formdesign/schema";
import { useTabTitle } from "../../tabs/tabContext";

import { PageHeader } from "../../components/PageHeader";

const { Text } = Typography;

/**
 * The form designer, on antd 5.
 *
 * The original designer is a separate React app pinned to antd 4, because the
 * library it is built on (`@designable`) has not been released since 2022 and
 * cannot run on antd 5. This is the replacement being grown in-app, on the same
 * `@formily/antd-v5` renderer the permits themselves already use.
 *
 * Both designers write the same schema. Anything this one has no editor for is
 * carried through untouched (see `schema.ts`), so a form can be opened here,
 * edited, saved, and still opened in the old designer afterwards. Until this
 * one covers everything, that is the property that matters most.
 */
const FormDesignerPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const formId = Number(id);

  const [form, setForm] = useState<any>();
  const [doc, setDoc] = useState<Doc | undefined>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const savedRef = useRef("");

  useTabTitle(form?.title);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await ApiService.apiMainFormsRetrieve(formId);
        setForm(data);
        const parsed = parse((data as any).schema_json);
        setDoc(parsed);
        savedRef.current = JSON.stringify(parsed);
      } catch (e: any) {
        message.error(e?.body?.message ?? t("Something went wrong"));
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const dirty = useMemo(
    () => !!doc && JSON.stringify(doc) !== savedRef.current,
    [doc]
  );

  // Warn before losing edits — this page holds the only copy until Save.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const edit = (fn: (d: Doc) => void) =>
    setDoc((current) => {
      if (!current) return current;
      const next: Doc = JSON.parse(JSON.stringify(current));
      fn(next);
      return next;
    });

  const selected = doc && selectedId ? find(doc.root, selectedId) : null;

  /**
   * The questions asked before the selected one, flattened out of the tree.
   *
   * Document order, not tree order: a person fills a form top to bottom, so
   * "earlier" means earlier on the page whatever container it sits in. Cut at
   * the selected field, because a rule reading an answer given *below* it can
   * never be true when it is first evaluated -- the same mistake the process
   * builder guards against for stage conditions.
   */
  const earlierFields = (() => {
    if (!doc || !selectedId) return [];
    const flat: Array<{ key: string; title: string; options: string[]; id: string }> = [];
    const walk = (nodes: Node[]) => {
      nodes.forEach((n) => {
        if (!isContainer(n)) {
          flat.push({
            key: n.key,
            title: n.title || n.key,
            // Option VALUES, because that is what the answer is stored as and
            // therefore what the generated expression has to compare against.
            options: (n.enumOptions ?? []).map((o) => o.value),
            id: n.id,
          });
        }
        walk(n.children);
      });
    };
    walk(doc.root);
    const at = flat.findIndex((f) => f.id === selectedId);
    return (at === -1 ? flat : flat.slice(0, at)).map(({ id, ...rest }) => rest);
  })();

  /** Where a new field lands: inside the selection if it can hold children,
   *  otherwise beside it, otherwise in the last container on the canvas. */
  const dropTarget = (d: Doc): Node | null => {
    if (selectedId) {
      const node = find(d.root, selectedId);
      if (node && isContainer(node)) return node;
      if (node) {
        const at = locate(d.root, selectedId);
        if (at?.parent) return at.parent;
      }
    }
    const containers = d.root.filter(isContainer);
    if (containers.length === 0) return null;
    // Walk to the innermost trailing container, which is where the last field
    // was almost certainly added.
    let node = containers[containers.length - 1];
    while (true) {
      const nested = node.children.filter(isContainer);
      if (nested.length === 0) return node;
      node = nested[nested.length - 1];
    }
  };

  const addField = (id: string) => {
    const widget = WIDGETS.find((w) => widgetId(w) === id);
    if (!widget || !doc) return;
    let newId: string | null = null;
    edit((d) => {
      const field = makeField(widget, allKeys(d.root));
      newId = field.id;
      const target = dropTarget(d);
      // A repeating table's contents are columns, not loose fields: dropping a
      // field straight in would produce a node the renderer draws nowhere. The
      // field goes to the table's own parent instead, which is where somebody
      // pointing at a table almost certainly meant it.
      if (target && isTable(target)) {
        const at = locate(d.root, target.id);
        if (at?.parent) at.parent.children.push(field);
        else d.root.push(field);
      } else if (target) target.children.push(field);
      else d.root.push(field);
    });
    if (newId) setSelectedId(newId);
  };

  /** Add a layout container -- a card, a grid, a repeating table, static text. */
  const addLayout = (id: string) => {
    const widget = LAYOUT_WIDGETS.find((w) => widgetId(w) === id);
    if (!widget || !doc) return;
    let newId: string | null = null;
    edit((d) => {
      const container = makeContainer(widget, allKeys(d.root));
      newId = container.id;
      const target = dropTarget(d);
      // A container inside a repeating table is not a thing the renderer draws,
      // so it lands beside the table rather than inside it.
      if (target && !isTable(target)) target.children.push(container);
      else d.root.push(container);
    });
    if (newId) setSelectedId(newId);
  };

  /** One more column on the selected table. */
  const addColumn = () => {
    if (!doc || !selectedId) return;
    let newId: string | null = null;
    edit((d) => {
      const table = find(d.root, selectedId);
      if (!table || !isTable(table)) return;
      const column = makeColumn(
        `Column ${table.itemChildren.length + 1}`,
        allKeys(d.root)
      );
      newId = column.id;
      table.itemChildren.push(column);
    });
    if (newId) setSelectedId(newId);
  };

  const save = async (publish: boolean) => {
    if (!doc) return;
    setSaving(true);
    try {
      await ApiService.apiMainFormVersionsCreate(formId, {
        schema_json: serialise(doc),
        publish,
        note: "",
      });
      savedRef.current = JSON.stringify(doc);
      // Keep the local doc as the source of truth: the server echoes the schema
      // back, but re-parsing it would throw away the current selection.
      setDoc({ ...doc });
      message.success(
        publish ? t("Form published") : t("Draft saved")
      );
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!doc || !form) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("This form could not be loaded.")}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title={t(form.title)}
        meta={
          <>
            <Tag style={{ marginInlineEnd: 0 }}>{form.type}</Tag>
            {dirty && (
              <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                {t("Unsaved changes")}
              </Tag>
            )}
          </>
        }
        actions={
          <Space wrap>
            <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
              {t("Preview")}
            </Button>
            <Button
              loading={saving}
              disabled={!dirty}
              onClick={() => save(false)}
            >
              {t("Save draft")}
            </Button>
            <Button type="primary" loading={saving} onClick={() => save(true)}>
              {t("Publish")}
            </Button>
          </Space>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t(
          "Publishing a form only affects permits created afterwards. Permits already in flight keep the version they started on."
        )}
      />

      <Row gutter={16} align="top">
        <Col xs={24} lg={5}>
          <Card size="small" title={t("Add a field")}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {WIDGETS.map((w) => (
                <Button
                  key={widgetId(w)}
                  size="small"
                  block
                  icon={<PlusOutlined />}
                  style={{ textAlign: "left" }}
                  onClick={() => addField(widgetId(w))}
                >
                  {t(w.label)}
                </Button>
              ))}
            </Space>
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginTop: 10 }}
            >
              {t("A new field lands in whatever is selected on the canvas.")}
            </Text>
          </Card>

          {/* Kept as a second group rather than more buttons in the first: a
              card and a text box are not questions, and a palette that mixes
              them reads as one long list of twelve unrelated things. */}
          <Card size="small" title={t("Layout")} style={{ marginTop: 12 }}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {LAYOUT_WIDGETS.map((w) => (
                <Button
                  key={widgetId(w)}
                  size="small"
                  block
                  icon={<PlusOutlined />}
                  style={{ textAlign: "left" }}
                  onClick={() => addLayout(widgetId(w))}
                >
                  {t(w.label)}
                </Button>
              ))}
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card size="small" title={t("The form")}>
            <DesignCanvas
              root={doc.root}
              selectedId={selectedId}
              onSelect={setSelectedId}
              lookup={(nodeId) => find(doc.root, nodeId)}
              parentOf={(nodeId) => {
                const at = locate(doc.root, nodeId);
                return at
                  ? { parentId: at.parent ? at.parent.id : null, index: at.index }
                  : null;
              }}
              onMove={({ id: movedId, targetParentId, index }) =>
                edit((d) => {
                  moveNode(d.root, movedId, targetParentId, index);
                })
              }
            />
          </Card>
        </Col>

        <Col xs={24} lg={7}>
          <Card size="small" title={t("Settings")}>
            <FieldInspector
              onAddColumn={addColumn}
              node={selected}
              earlierFields={earlierFields}
              onChange={(patch) =>
                edit((d) => {
                  const node = selectedId ? find(d.root, selectedId) : null;
                  if (node) Object.assign(node, patch);
                })
              }
              onRemove={() =>
                edit((d) => {
                  if (selectedId) removeNode(d.root, selectedId);
                  setSelectedId(null);
                })
              }
            />
          </Card>
        </Col>
      </Row>

      {/* The preview is the real renderer, not an approximation — the same
          component that draws this form on a permit. */}
      <Drawer
        title={t("Preview")}
        width={Math.min(820, window.innerWidth - 80)}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        destroyOnClose
      >
        <SchemaPreview
          schema={serialise(doc).schema}
          formProps={doc.form}
        />
      </Drawer>
    </div>
  );
};

export default FormDesignerPage;
